/**
 * Slide-out side panel — thin wrapper over `@radix-ui/react-dialog` with
 * right-anchored positioning. Same primitives as the regular `Dialog` (so
 * close-on-escape, focus trap, overlay click all come for free); just
 * different visual treatment.
 *
 * Modeled after the shadcn-ui Sheet component but trimmed to what the
 * Nutrients drilldown needs — single right-side variant, no animation
 * orchestrator. Add side-prop support if a later caller needs left/top/bottom.
 */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Default `modal={false}` so the surrounding page stays interactive while
 * the sheet is open (no focus trap, no pointer-events block, no overlay
 * blur). Callers that want true-modal behavior can still pass `modal`
 * explicitly. ESC still closes via Radix's escape handler.
 */
export const Sheet = ({
  modal = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root modal={modal} {...props} />
);
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;

export const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = 'SheetOverlay';

type SheetContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Width of the panel. Defaults to 420px — wide enough for a chart + meal list. */
  widthClass?: string;
};

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, children, widthClass = 'w-[420px]', ...props }, ref) => (
  <SheetPortal>
    {/* No SheetOverlay — we want the underlying page (e.g. Nutrients table)
        to stay visible and clickable while the sheet is open. */}
    <DialogPrimitive.Content
      // Stop Radix from auto-focusing inside the panel when it opens; with
      // modal=false the page is interactive and stealing focus from the
      // table-row click that opened the drilldown is jarring. The X close
      // button is still tab-reachable.
      onOpenAutoFocus={(e) => e.preventDefault()}
      ref={ref}
      // Inline `style` for the right-edge dock so we don't depend on
      // tailwind's data-state selectors being picked up by the JIT scan.
      // Tailwind classes still handle the visual stuff (border / shadow / bg).
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, maxWidth: '100vw', zIndex: 50 }}
      className={cn(
        'flex h-full flex-col gap-4 border-l bg-background shadow-xl',
        widthClass,
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-3 top-3 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = 'SheetContent';

export const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1.5 px-5 pt-5', className)} {...props} />
);

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-xs text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';
